/**
 * One-shot first-launch reveal for the floating action burst.
 *
 * On the user's first visit we briefly open the burst to hint at its
 * discoverability, then close it a short moment later and persist that
 * it has been seen. Subsequent launches skip the reveal entirely.
 *
 * Isolating this orchestration into a hook keeps `FloatingMenu` free of
 * timer plumbing and makes the reveal trivially observable / mockable.
 */

import { useEffect } from "react";
import { fabUiSlice, usePersistedSlice } from "../../../store/persistence";
import { AUTO_OPEN_DURATION_MS } from "./floatingActionBurst.configuration";

/** Options for the first-launch reveal hook. */
export interface UseBurstAutoOpenRevealOptions {

    /**
     * Setter for the burst open state. Called twice during the reveal:
     * once to open, once to close after `AUTO_OPEN_DURATION_MS`.
     */
    "setOpen": ( open: boolean ) => void;
}

/**
 * Run the one-shot first-launch reveal when the `hasSeenFabBurst` flag
 * is still `false`. Reveal is idempotent: it runs exactly once per
 * device.
 *
 * @param options Configuration. See `UseBurstAutoOpenRevealOptions`.
 */
export const useBurstAutoOpenReveal = ( options: UseBurstAutoOpenRevealOptions ): void => {
    const { setOpen } = options;
    const [ fabUi, setFabUi ] = usePersistedSlice( fabUiSlice );
    const hasSeenFabBurst = fabUi.hasSeenFabBurst;

    useEffect( () => {
        if ( hasSeenFabBurst ) {
            return;
        }

        /*
         * Both state updates are deferred to a timer so they never run
         * synchronously within the effect body (react-hooks lint rule),
         * and to give React a frame to mount the burst before painting
         * the "open" phase.
         */
        const openTimerId = window.setTimeout( () => {
            setOpen( true );
        }, 0 );
        const closeTimerId = window.setTimeout( () => {
            setOpen( false );
            setFabUi( ( previous ) => ( {
                ...previous,
                "hasSeenFabBurst": true,
            } ) );
        }, AUTO_OPEN_DURATION_MS );

        return () => {
            window.clearTimeout( openTimerId );
            window.clearTimeout( closeTimerId );
        };
    }, [ hasSeenFabBurst, setFabUi, setOpen ] );
};
